import fs from 'node:fs';
import os from 'node:os';
import path from 'node:path';
import { DEFAULT_PRODUCT_NAME } from './constants.js';
import { adminUrlFromAppConfig } from './lib/appConfig.js';

export interface Branding {
  productName: string;
  logoUrl?: string | null;
  colors?: { primary: string; accent?: string; secondary?: string } | null;
}

export interface Profile {
  email: string;
  token: string;
  /** Tenant slug, when known (login --company). */
  company?: string;
  branding: Branding;
  createdAt: string;
}

export interface ProfilesFile {
  version: 1;
  defaultAdminUrl?: string;
  profiles: Record<string, Profile>;
}

const EMPTY: ProfilesFile = { version: 1, profiles: {} };

export function profilesDir(homedir: string = os.homedir()): string {
  return path.join(homedir, '.ghosty');
}

export function profilesPath(homedir: string = os.homedir()): string {
  return path.join(profilesDir(homedir), 'profiles.json');
}

/** Normalize an admin URL into a stable profile key (no trailing slash). */
export function normalizeAdminUrl(adminUrl: string): string {
  const url = new URL(adminUrl);
  return `${url.protocol}//${url.host}`;
}

export function loadProfiles(homedir?: string): ProfilesFile {
  const file = profilesPath(homedir);
  if (!fs.existsSync(file)) return structuredClone(EMPTY);
  try {
    const parsed = JSON.parse(fs.readFileSync(file, 'utf8')) as Partial<ProfilesFile>;
    if (parsed.version !== 1 || typeof parsed.profiles !== 'object' || !parsed.profiles) {
      return structuredClone(EMPTY);
    }
    return { version: 1, defaultAdminUrl: parsed.defaultAdminUrl, profiles: parsed.profiles };
  } catch {
    return structuredClone(EMPTY);
  }
}

export function saveProfiles(data: ProfilesFile, homedir?: string): void {
  const dir = profilesDir(homedir);
  fs.mkdirSync(dir, { recursive: true, mode: 0o700 });
  const file = profilesPath(homedir);
  fs.writeFileSync(file, `${JSON.stringify(data, null, 2)}\n`);
  // Token file should be owner-only. Windows ACLs don't map onto POSIX
  // modes — chmod is a silent no-op there, which is fine.
  try {
    fs.chmodSync(file, 0o600);
  } catch {
    /* Windows / exotic filesystems: skip silently */
  }
}

export function upsertProfile(
  adminUrl: string,
  profile: Profile,
  homedir?: string,
): ProfilesFile {
  const key = normalizeAdminUrl(adminUrl);
  const data = loadProfiles(homedir);
  data.profiles[key] = profile;
  data.defaultAdminUrl = key;
  saveProfiles(data, homedir);
  return data;
}

export interface ResolvedProfile {
  adminUrl: string;
  profile: Profile;
}

/**
 * Resolve the profile to act with: explicit --admin-url wins, else the saved
 * default profile, else — as a last resort — the admin URL the current app
 * folder carries (app.config.json), when a profile for it already exists.
 * Returns null when nothing matches.
 *
 * Note the asymmetry with login's resolveAdminUrl: there the app folder's URL
 * beats the saved default (login PICKS a tenant). Here a post-login command
 * trusts an established default first, and only falls back to the folder's URL
 * to rescue running in a fresh download before any default is set.
 */
export function resolveProfile(
  adminUrlFlag?: string,
  homedir?: string,
  dir?: string,
): ResolvedProfile | null {
  const data = loadProfiles(homedir);
  if (adminUrlFlag) {
    const key = normalizeAdminUrl(adminUrlFlag);
    const profile = data.profiles[key];
    return profile ? { adminUrl: key, profile } : null;
  }
  if (data.defaultAdminUrl && data.profiles[data.defaultAdminUrl]) {
    return { adminUrl: data.defaultAdminUrl, profile: data.profiles[data.defaultAdminUrl] };
  }
  const fromApp = adminUrlFromAppConfig(dir ?? process.cwd());
  if (fromApp) {
    try {
      const key = normalizeAdminUrl(fromApp);
      const profile = data.profiles[key];
      if (profile) return { adminUrl: key, profile };
    } catch {
      /* malformed app.config URL — nothing to resolve */
    }
  }
  return null;
}

/** Product name for human-facing output; cached branding, else the default. */
export function productName(adminUrlFlag?: string, homedir?: string): string {
  const resolved = resolveProfile(adminUrlFlag, homedir);
  return resolved?.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;
}
